import {
  Action,
  type FindCaseRecordsRequest,
  type OutputFieldCondition,
  SearchField,
  type SearchSortField,
} from '@compass/client/types/case_record';
import type { Sort } from '@compass/client/types/common';
import { caseRecordService } from '@services/api';
import { createCaseRecordWithFiles } from '@services/caseRecordFiles.service';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { CASES_QUERY_CONFIG } from '@/constants/queryConfig';
import type { ActionDataParams } from '@/types/actions';
import { buildActionData, getServiceMethodForAction } from '@/utils/caseActions';
import { removeOrgPrefix, removeProgPrefix } from '@/utils/routes';

/**
 * Loads one case record.
 *
 * The case is fetched under the organization and program in the route
 * (`/org-:orgId/prog-:progId/...`), falling back to the selected ones only on routes without
 * them. The store can still hold the previous program when a case page mounts: My Cases lists
 * every program's cases, and the route-to-store sync runs after the first render. Fetching with
 * the store's program then 404s, and because retries are off and the key is the case id alone,
 * the page stayed on "Something went wrong" (BV-2597, prod run 40645698070).
 *
 * The key stays the case id: a case belongs to exactly one program, and the optimistic and
 * live updates (useWatchCaseRecord, useDispositions, the mutations below) address it by that
 * exact key.
 */
export const useCase = (
  caseId: string | null,
  options?: { refetchInterval?: number | false; refetchOnMount?: boolean | 'always' },
) => {
  const { orgId: routeOrgId, progId: routeProgId } = useParams<{
    orgId?: string;
    progId?: string;
  }>();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);
  const organizationId = routeOrgId ? removeOrgPrefix(routeOrgId) : selectedOrganizationId;
  const programId = routeProgId ? removeProgPrefix(routeProgId) : selectedProgramId;

  const isEnabled = useMemo(
    () => Boolean(caseId && organizationId && programId),
    [caseId, organizationId, programId],
  );

  return useQuery({
    queryKey: queryKeys.cases.byId(caseId || ''),
    queryFn: () =>
      caseRecordService.getCaseRecord({
        organization_id: organizationId!,
        program_id: programId!,
        case_record_id: caseId!,
      }),
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - same as global default
    gcTime: 10 * 60 * 1000, // 10 minutes - same as global default
    refetchInterval: options?.refetchInterval,
    refetchOnMount: options?.refetchOnMount,
  });
};

// Helper to build query key params for cases query
const buildCasesQueryKeyParams = (params: FindCaseRecordsRequest, limit: number) => ({
  limit,
  start_after_id: params.start_after_id,
  states: params.states,
  referenceIds: params.reference_ids,
  caseIds: params.case_record_ids,
  caseCodes: params.case_codes,
  assigned: params.assigned_user_ids,
  created_at_after: params.created_at_after,
  created_at_before: params.created_at_before,
  campaign_id: params.campaign_id,
  payor_name: params.payor_name,
  sort_by_created_at: params.sort_by_created_at,
  sort_by_payor_name: params.sort_by_payor_name,
});

// Helper to compute cursor pagination info
const computeCursorPaginationInfo = <T extends { id?: string }>(
  caseRecords: T[] | undefined,
  limit: number,
) => {
  const records = caseRecords ?? [];
  const recordsCount = records.length;
  const hasMore = recordsCount === limit;
  const nextCursor = hasMore && recordsCount > 0 ? records[recordsCount - 1]?.id : undefined;
  return { records, hasMore, nextCursor };
};

export const useCasesByWorkflow = (params: FindCaseRecordsRequest) => {
  const { setCases } = useStore();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  // Server caps `FindCaseRecords.limit` at 100 (LA-7313); a larger default is
  // silently clamped, so match the cap.
  const limit = params.limit ?? 100;
  const queryParams: FindCaseRecordsRequest = useMemo(
    () => ({ ...params, limit }),
    [params, limit],
  );

  const isEnabled = Boolean(params.workflow_id && selectedOrganizationId && selectedProgramId);

  const query = useQuery({
    queryKey: queryKeys.cases.byWorkflow(
      params.workflow_id!,
      buildCasesQueryKeyParams(params, limit),
    ),
    queryFn: () =>
      caseRecordService.findCaseRecords({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        ...queryParams,
      }),
    enabled: isEnabled,
    ...CASES_QUERY_CONFIG,
  });

  // Sync with Zustand store
  useEffect(() => {
    if (query.data?.case_records) {
      setCases(query.data.case_records);
    }
  }, [query.data, setCases]);

  const { records, hasMore, nextCursor } = computeCursorPaginationInfo(
    query.data?.case_records,
    limit,
  );
  const totalCount = query.data?.total_count ?? 0;

  return {
    ...query,
    cases: records,
    totalCount,
    hasMore,
    nextCursor,
    data: query.data ? { ...query.data, has_more: hasMore } : undefined,
  };
};

// Map field names to SearchField enum values
const FIELD_NAME_TO_SEARCH_FIELD: Record<string, SearchField> = {
  reporter_phone: SearchField.SEARCH_FIELD_REPORTER_PHONE,
  payor_name: SearchField.SEARCH_FIELD_PAYOR_NAME,
  patient_full_name: SearchField.SEARCH_FIELD_PATIENT_FULL_NAME,
  agent_name: SearchField.SEARCH_FIELD_AGENT_NAME,
  first_name: SearchField.SEARCH_FIELD_FIRST_NAME,
  primary_payer_name: SearchField.SEARCH_FIELD_PRIMARY_PAYER_NAME,
  primary_phone: SearchField.SEARCH_FIELD_PRIMARY_PHONE,
  primary_member_id: SearchField.SEARCH_FIELD_PRIMARY_MEMBER_ID,
  secondary_member_id: SearchField.SEARCH_FIELD_SECONDARY_MEMBER_ID,
  practice_name: SearchField.SEARCH_FIELD_PRACTICE_NAME,
  j_code: SearchField.SEARCH_FIELD_J_CODE,
  drug_name: SearchField.SEARCH_FIELD_DRUG_NAME,
  provider_first_name: SearchField.SEARCH_FIELD_PROVIDER_FIRST_NAME,
  phone: SearchField.SEARCH_FIELD_PHONE,
  member_id: SearchField.SEARCH_FIELD_MEMBER_ID,
  npi: SearchField.SEARCH_FIELD_NPI,
  client_reference_number: SearchField.SEARCH_FIELD_CLIENT_REFERENCE_NUMBER,
  mrn: SearchField.SEARCH_FIELD_MRN,
  provider_last_name: SearchField.SEARCH_FIELD_PROVIDER_LAST_NAME,
  date_of_service: SearchField.SEARCH_FIELD_DATE_OF_SERVICE,
  last_name: SearchField.SEARCH_FIELD_LAST_NAME,
  phone_mobile: SearchField.SEARCH_FIELD_PHONE_MOBILE,
  phone_home: SearchField.SEARCH_FIELD_PHONE_HOME,
  reachout_eligibility_status: SearchField.SEARCH_FIELD_REACHOUT_ELIGIBILITY_STATUS,
  ineligibility_tag: SearchField.SEARCH_FIELD_INELIGIBILITY_TAG,
  medical_record_number: SearchField.SEARCH_FIELD_MEDICAL_RECORD_NUMBER,
  payer_name: SearchField.SEARCH_FIELD_PAYER_NAME,
  // PA Status workflows (pa_status_pbm / pa_status_medbv) prefix their patient
  // fields, so they cannot reuse first_name/last_name/member_id above — those
  // point at different schema keys. See BV-2092.
  patient_first_name: SearchField.SEARCH_FIELD_PATIENT_FIRST_NAME,
  patient_last_name: SearchField.SEARCH_FIELD_PATIENT_LAST_NAME,
  patient_member_id: SearchField.SEARCH_FIELD_PATIENT_MEMBER_ID,
  drug_ndc: SearchField.SEARCH_FIELD_DRUG_NDC,
  // j_codes/cpt_codes are arrays of objects in the schema, so the column's
  // fieldPath ends at the array. The enum resolves it to the `.code` sub-field
  // server-side (the same indirection agent_name uses).
  j_codes: SearchField.SEARCH_FIELD_J_CODES_CODE,
  cpt_codes: SearchField.SEARCH_FIELD_CPT_CODES_CODE,
  // PA Status output fields (BV-2092). These leaf names are identical on both
  // workflows — pa_status_pbm carries them flat on output_data.data, pa_status_medbv
  // repeats them inside each drug[]/procedure[] entry — so one entry serves both and
  // the server ORs across all three paths. pa_status_medbv has no COLUMN with these
  // leaves (its schema stops at the drug/procedure array), so unlike the fields above
  // these are also offered unconditionally from CasesContainer's workflow-gated block.
  prior_authorization_on_file_status: SearchField.SEARCH_FIELD_PA_STATUS,
  prior_authorization_required: SearchField.SEARCH_FIELD_PA_REQUIRED,
  prior_authorization_on_file_exists: SearchField.SEARCH_FIELD_PA_ON_FILE_EXISTS,
  appeal_available: SearchField.SEARCH_FIELD_APPEAL_AVAILABLE,
  appeal_status: SearchField.SEARCH_FIELD_APPEAL_STATUS,
  // BHO output field (BV-2505): whether behavioral health is carved out to a separate
  // policy (yes / no / unknown). Searchable once its column is selected.
  seperate_bho_policy: SearchField.SEARCH_FIELD_SEPERATE_BHO_POLICY,
};

// Fields supported by the /search/advanced API (Atlas Search) - shown in unified search dropdown
const ADVANCED_SEARCH_FIELDS = new Set(Object.keys(FIELD_NAME_TO_SEARCH_FIELD));

// Additional search fields not shown in unified search (e.g., case_type is a dropdown filter)
const ADDITIONAL_SEARCH_FIELDS: Record<string, SearchField> = {
  case_type: SearchField.SEARCH_FIELD_CASE_TYPE,
};

// Extract field name from path (e.g., 'input_data.data.reporter_phone' -> 'reporter_phone')
export const getFieldNameFromPath = (fieldPath: string): string => {
  const parts = fieldPath.split('.');
  return parts[parts.length - 1];
};

export const isAdvancedSearchField = (fieldPath: string): boolean => {
  const fieldName = getFieldNameFromPath(fieldPath);
  return ADVANCED_SEARCH_FIELDS.has(fieldName);
};

// Convert field name to SearchField enum
// Checks both unified search fields and additional fields (like case_type dropdown)
export const getSearchFieldEnum = (fieldName: string): SearchField | null => {
  return FIELD_NAME_TO_SEARCH_FIELD[fieldName] ?? ADDITIONAL_SEARCH_FIELDS[fieldName] ?? null;
};

// Helper to normalize case_type_filter - treat 'all' as undefined
const normalizeCaseTypeFilter = (filter?: string): string | undefined =>
  filter && filter !== 'all' ? filter : undefined;

interface AdvancedSearchParams {
  field: string;
  value: string;
  workflow_id?: string;
  limit?: number;
  created_at_after?: string;
  created_at_before?: string;
  assigned_user_ids?: string[];
  case_type_filter?: string;
  // PQC lifecycle-tab params: when output_conditions/states are present the query
  // is driven by the tab (not the search box), with keyset pagination via page_token.
  states?: string[];
  output_conditions?: OutputFieldCondition[];
  sort_field?: SearchSortField;
  sort_order?: Sort;
  page_token?: string;
}

// eslint-disable-next-line complexity -- text/case-type/output-condition modes add guards to one query hook
export const useCasesByAdvancedSearch = (params: AdvancedSearchParams) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  const limit = params.limit ?? 100;
  const caseTypeFilter = normalizeCaseTypeFilter(params.case_type_filter);
  const searchFieldEnum = getSearchFieldEnum(params.field);
  const hasTextSearch = searchFieldEnum !== null && !!params.value;
  const hasCaseTypeFilter = !!caseTypeFilter;
  const hasOutputConditions = !!params.output_conditions?.length;

  // Enable query if we have text search, case type filter, or output conditions.
  const isEnabled =
    (hasTextSearch || hasCaseTypeFilter || hasOutputConditions) &&
    !!selectedOrganizationId &&
    !!selectedProgramId;

  const query = useQuery({
    queryKey: [
      'cases',
      'advancedSearch',
      params.field,
      params.value,
      params.workflow_id,
      limit,
      params.created_at_after,
      params.created_at_before,
      params.assigned_user_ids,
      caseTypeFilter,
      params.states,
      params.output_conditions,
      params.sort_field,
      params.sort_order,
      params.page_token,
    ],
    queryFn: () =>
      caseRecordService.searchCaseRecords({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        field: searchFieldEnum ?? undefined,
        value: params.value || undefined,
        workflow_id: params.workflow_id,
        limit,
        created_at_after: params.created_at_after,
        created_at_before: params.created_at_before,
        assigned_user_ids: params.assigned_user_ids,
        case_type_filter: caseTypeFilter,
        states: params.states && params.states.length > 0 ? params.states : undefined,
        output_conditions: params.output_conditions,
        sort_field: params.sort_field,
        sort_order: params.sort_order,
        page_token: params.page_token,
      }),
    enabled: isEnabled,
    ...CASES_QUERY_CONFIG,
  });

  const records = query.data?.case_records ?? [];
  // Keyset pagination: the server returns next_page_token when more pages exist.
  const nextToken = query.data?.next_page_token || undefined;

  return {
    ...query,
    cases: records,
    totalCount: records.length,
    hasMore: !!nextToken,
    nextCursor: nextToken,
    data: query.data ? { ...query.data, has_more: !!nextToken } : undefined,
  };
};

export const useCreateCase = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useMutation({
    mutationFn: (params: {
      workflowId: string;
      referenceId?: string;
      inputData: any;
      /** File-type field uploads (fieldId -> File) for the Manual Case Creation flow. */
      files?: Record<string, File | File[]>;
    }) => {
      const request = {
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        workflow_id: params.workflowId,
        reference_id: params.referenceId,
        input_data: params.inputData,
      };
      // When File-type fields were filled, create the case and upload the files in one multipart
      // request; otherwise use the plain JSON create.
      if (params.files && Object.keys(params.files).length > 0) {
        return createCaseRecordWithFiles(request, params.files);
      }
      return caseRecordService.createCaseRecord(request);
    },
    onSuccess: (data, _variables) => {
      // Invalidate the list queries to refresh the table
      queryClient.invalidateQueries({
        queryKey: queryKeys.cases.all,
      });

      // Invalidate case counts queries to refresh filter counts
      queryClient.invalidateQueries({
        queryKey: ['quickFilterCounts'],
      });

      toast.success('Case created successfully');
      return data;
    },

    onError: (error: any) => {
      const errorCode = error.response?.status || error.code;
      const suffix = errorCode ? ` (Error: ${errorCode})` : '';
      toast.error(`${createCaseErrorDetail(error)}${suffix}`);
    },
  });
};

/**
 * Extract the most specific failure reason from a create-case error. Prefers
 * the server's sanitized error detail over the static label: for file
 * validation failures it names the exact rule and file (e.g. "file content
 * (image/jpeg) does not match its .pdf extension"), which the user needs in
 * order to fix the submission.
 */
const createCaseErrorDetail = (error: any): string => {
  const data = error?.response?.data;
  return data?.error || data?.message || 'Failed to create case. Please try again.';
};

export const useCloneCase = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useMutation({
    mutationFn: ({ caseId }: { caseId: string }) =>
      caseRecordService.cloneCaseRecord({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        case_record_id: caseId,
      }),
    onSuccess: data => {
      // Invalidate the list queries to refresh the table
      queryClient.invalidateQueries({
        queryKey: queryKeys.cases.all,
      });

      // Invalidate case counts queries to refresh filter counts
      queryClient.invalidateQueries({
        queryKey: ['quickFilterCounts'],
      });

      toast.success('Case cloned successfully');

      // Navigate to the cloned case if ID is returned
      if (data.case_record_id) {
        navigate(`../cases/${data.case_record_id}`);
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to clone case');
    },
  });
};

export const useUpdateCaseOutput = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useMutation({
    mutationFn: ({ caseId, outputData }: { caseId: string; outputData: any }) =>
      caseRecordService.updateOutputData({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        case_record_id: caseId,
        output_data: outputData,
      }),
    onMutate: async ({ caseId, outputData }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.cases.byId(caseId) });

      // Snapshot the previous value
      const previousCase = queryClient.getQueryData(queryKeys.cases.byId(caseId));

      // Optimistically update (merge semantics). The cached GetCaseRecordResponse IS the
      // case — flat, with `output_data` at the top level; there is no `case_record`
      // wrapper. This used to require one, so it never applied: the page kept showing the
      // pre-write output until the refetch landed, and any write built on that copy in the
      // meantime (a second audit Revert a second later) re-sent stale values and undid the
      // first (LA-7951).
      queryClient.setQueryData(queryKeys.cases.byId(caseId), (old: any) => {
        if (!old) return old;
        return {
          ...old,
          output_data: {
            ...old.output_data,
            data: { ...old.output_data?.data, ...outputData.data },
          },
        };
      });

      // Return a context object with the snapshotted value
      return { previousCase, caseId };
    },
    onError: (err, { caseId }, context) => {
      // If the mutation fails, use the context to roll back
      if (context?.previousCase) {
        queryClient.setQueryData(queryKeys.cases.byId(caseId), context.previousCase);
      }
      toast.error(err.message || 'Failed to update Output');
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch the specific case
      queryClient.invalidateQueries({
        queryKey: queryKeys.cases.byId(variables.caseId),
        refetchType: 'all',
      });

      // Invalidate the list queries to refresh the table
      queryClient.invalidateQueries({
        queryKey: queryKeys.cases.all,
        refetchType: 'all',
      });

      // Invalidate case counts queries to refresh filter counts
      queryClient.invalidateQueries({
        queryKey: ['quickFilterCounts'],
      });
    },
  });
};

/**
 * Clears the case record's persisted validation error (set by the PQC agent)
 * via UpdateCaseRecord. Review UIs call this after a successful save while the
 * case is in review with an error set — the workflow re-validates at
 * submission, so nothing is lost.
 */
export const useClearCaseError = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useMutation({
    mutationFn: ({ caseId }: { caseId: string }) =>
      caseRecordService.updateCaseRecord({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        case_record_id: caseId,
        clear_error: true,
      }),
    onSuccess: (_data, { caseId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cases.byId(caseId) });
    },
  });
};

export const useUpdateCaseInput = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useMutation({
    mutationFn: ({ caseId, inputData }: { caseId: string; inputData: any }) =>
      caseRecordService.updateInputData({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        case_record_id: caseId,
        input_data: inputData,
      }),
    onMutate: async ({ caseId, inputData }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.cases.byId(caseId) });

      // Snapshot the previous value
      const previousCase = queryClient.getQueryData(queryKeys.cases.byId(caseId));

      // Optimistically update (merge semantics)
      queryClient.setQueryData(queryKeys.cases.byId(caseId), (old: any) => {
        if (!old?.case_record) return old;
        return {
          ...old,
          case_record: {
            ...old.case_record,
            input_data: {
              ...old.case_record.input_data,
              data: { ...old.case_record.input_data?.data, ...inputData.data },
            },
          },
        };
      });

      // Return a context object with the snapshotted value
      return { previousCase, caseId };
    },
    onError: (err, { caseId }, context) => {
      // If the mutation fails, use the context to roll back
      if (context?.previousCase) {
        queryClient.setQueryData(queryKeys.cases.byId(caseId), context.previousCase);
      }
      toast.error(err.message || 'Failed to update Input');
    },
    onSuccess: (_, variables) => {
      // Invalidate and immediately refetch the specific case
      queryClient.invalidateQueries({
        queryKey: queryKeys.cases.byId(variables.caseId),
        refetchType: 'all', // Force immediate refetch regardless of staleTime
      });

      // Invalidate the list queries to refresh the table
      queryClient.invalidateQueries({
        queryKey: queryKeys.cases.all,
        refetchType: 'all',
      });
    },
  });
};

export const useUpdateAssignment = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useMutation({
    mutationFn: ({
      caseId,
      assigned,
      noteContent,
      teamId,
    }: {
      caseId: string;
      assigned: string;
      noteContent?: string;
      teamId?: string;
    }) =>
      caseRecordService.updateAssigned({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        case_record_id: caseId,
        assigned,
        team_id: teamId || undefined,
        note: noteContent?.trim() ? { content: noteContent.trim() } : undefined,
      }),
    onSuccess: (_, variables) => {
      // Invalidate and refetch the specific case
      queryClient.invalidateQueries({
        queryKey: queryKeys.cases.byId(variables.caseId),
      });

      // Invalidate the list queries to refresh the table
      queryClient.invalidateQueries({
        queryKey: queryKeys.cases.all,
      });

      // Invalidate notes queries to fetch any new assignment_changed notes
      queryClient.invalidateQueries({
        queryKey: ['notes', variables.caseId],
      });

      // Invalidate case counts queries to refresh filter counts
      queryClient.invalidateQueries({
        queryKey: ['quickFilterCounts'],
      });

      toast.success('Task reassigned successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to reassign task');
    },
  });
};

/**
 * Cross-program variant of `useUpdateAssignment`: takes the organization and
 * program ids from the mutation vars (each case's own ids) instead of the
 * store's selected program. Used by org-wide / cross-program views such as the
 * Tier-2 org PQC list, where there is no single selected program in scope.
 */
export const useUpdateAssignmentForCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      programId,
      caseId,
      assigned,
      teamId,
      noteContent,
    }: {
      organizationId: string;
      programId: string;
      caseId: string;
      assigned: string;
      teamId?: string;
      noteContent?: string;
    }) =>
      caseRecordService.updateAssigned({
        organization_id: organizationId,
        program_id: programId,
        case_record_id: caseId,
        assigned,
        team_id: teamId || undefined,
        note: noteContent?.trim() ? { content: noteContent.trim() } : undefined,
      }),
    onSuccess: (_, variables) => {
      // Refresh the specific case, the list queries, and the quick-filter counts.
      queryClient.invalidateQueries({ queryKey: queryKeys.cases.byId(variables.caseId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cases.all });
      queryClient.invalidateQueries({ queryKey: ['quickFilterCounts'] });
      queryClient.invalidateQueries({ queryKey: ['notes', variables.caseId] });
      toast.success('Task reassigned successfully');
    },
    // Generic copy — HIPAA-safe (no server message that may carry PHI).
    onError: () => {
      toast.error('Failed to reassign case. Please try again.');
    },
  });
};

export const useExecuteAction = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useMutation({
    mutationFn: ({
      caseId,
      action,
      actionDataParams,
    }: {
      caseId: string;
      action: Action;
      actionDataParams?: ActionDataParams;
      skipToast?: boolean;
    }) => {
      // Input validation
      if (!caseId || caseId.trim() === '') {
        throw new Error('Case ID is required');
      }

      if (action === undefined || action === null) {
        throw new Error('Action is required');
      }

      const serviceMethod = getServiceMethodForAction(action);
      return serviceMethod({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        case_record_id: caseId,
        action,
        action_data: buildActionData(action, actionDataParams),
        note:
          actionDataParams?.actionNote && actionDataParams.actionNote.trim()
            ? { content: actionDataParams.actionNote.trim() }
            : undefined,
      });
    },
    onSuccess: (data, variables) => {
      if (data.success) {
        // Invalidate and refetch the specific case
        queryClient.invalidateQueries({
          queryKey: queryKeys.cases.byId(variables.caseId),
        });

        // Invalidate the list queries to refresh the table
        queryClient.invalidateQueries({
          queryKey: queryKeys.cases.all,
        });

        // Invalidate case counts queries to refresh filter counts
        queryClient.invalidateQueries({
          queryKey: ['quickFilterCounts'],
        });

        // Invalidate notes queries to fetch any new state_changed notes
        queryClient.invalidateQueries({
          queryKey: ['notes', variables.caseId],
        });

        // Analytics: success is implicit — the button/dropdown click that triggered this action
        // (StatusDropdown/StatusConfirmationDialog) already captures the interaction via CLICK;
        // there is no UPDATE/submit event type.

        // Only show default toast if not explicitly skipped
        if (!variables.skipToast) {
          toast.success('Action executed successfully');
        }
      } else {
        toast.error('Failed to execute action');
      }
    },
    onError: (error: any, variables) => {
      if (!variables.skipToast) {
        toast.error(error.response?.data?.message || 'Failed to execute action');
      }
    },
  });
};

/**
 * Hook to handle navigation after successful case creation.
 * Provides a reusable success handler that navigates to the cases list
 * and optionally executes a callback before navigation.
 *
 * @returns A callback function that handles post-creation success logic
 *
 * @example
 * ```tsx
 * const handleCaseSuccess = useCaseCreationSuccess();
 *
 * if (result?.case_record_id) {
 *   saveFormData(formData);
 *   handleCaseSuccess(result);
 * }
 * ```
 */
export const useCaseCreationSuccess = () => {
  const navigate = useNavigate();

  return useCallback(
    (
      data: { case_record_id?: string },
      options?: {
        onBeforeNavigate?: () => void;
        // When true, land on the new case's detail page instead of the cases list.
        // Direct-submission (skip_intake_draft) workflows use this so the agent goes
        // straight to the review/submit surface — one-and-done lifecycle (AQ-603).
        redirectToDetails?: boolean;
      },
    ) => {
      // Execute optional callback before navigation (e.g., close modal)
      options?.onBeforeNavigate?.();

      // Navigate after a short delay to let the success toast show.
      if (data?.case_record_id) {
        setTimeout(() => {
          if (options?.redirectToDetails) {
            navigate(`../cases/${data.case_record_id}`);
          } else {
            navigate('../cases', { state: { highlightCaseId: data.case_record_id } });
          }
        }, 1500);
      }
    },
    [navigate],
  );
};
